import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import Autocomplete from "react-google-autocomplete";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchGeocodeData, signupUser } from "../../../services/userService";
import Loader from "../../../components/Loader";
import { sendOtp } from "../../../services/sendOtpService";
import { useTheme } from "../../../context/ThemeContext";
import { isValidEmail, validatePassword, sanitizeString, normalizeEmail } from "../../../utils/validation";
// Use only the apiKey prop in Autocomplete. Make sure REACT_APP_GOOGLE_MAPS_API_KEY is set in your .env file.
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
// Component for handling user registration
function RegisterIndex() {
  // Initialize hooks and state
  const { login } = useAuth();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  // Detect navigation from login
  const navState = window.history.state || {};

  const { signup } = useAuth();
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(""); // State for email input
  const [password, setPassword] = useState(""); // State for password input
  const [fullName, setFullName] = useState(""); // State for full name input
  const [city, setCity] = useState(""); // State for city input
  const [state, setState] = useState(""); // State for state input
  const [longitude, setLongitude] = useState(""); // State for longitude
  const [latitude, setLatitude] = useState(""); // State for latitude
  const [placeId, setPlaceId] = useState(""); // State for place_id
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [image, setImage] = useState(null); // State for selected image file
  const [imagePreview, setImagePreview] = useState(null); // State for image preview URL
  const [termsAccepted, setTermsAccepted] = useState(false); // State for terms checkbox

  // Clear fields on mount or if coming from login
  useEffect(() => {
    // If navigation state indicates coming from login, clear all fields
    if (navState && navState.usr === "fromLogin") {
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setFullName("");
      setCity("");
      setState("");
      setLongitude("");
      setLatitude("");
      setPlaceId("");
      setImage(null);
      setImagePreview(null);
      setTermsAccepted(false);
    }
  }, []);

  const getStateShortCode = (province) => {
    if (!province) return "";
    return province.slice(0, 2).toUpperCase();
  };
  const handleGeocodeData = async (placeId) => {
    setLoadingAddress(true);
    const response = await fetchGeocodeData(placeId);

    if (response.success) {
      const { city, state, longitude, latitude, address_with_city_state } =
        response.data;
      const province = address_with_city_state.split(", ")[1] || state;
      const shortState = getStateShortCode(province);
      setState(shortState);
      setCity(city);
      setLongitude(longitude);
      setLatitude(latitude);
      setPlaceId(placeId);
    } else {
      setError(response.message);
    }
    setLoadingAddress(false);
  };

  // Handle image selection and preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission for registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate in order: First Name, Email, Password, Confirm Password, Terms
    if (!fullName || !fullName.trim()) {
      toast.error(t("full_name_required", "First Name is required."));
      return;
    }
    
    // Sanitize full name
    const sanitizedFullName = sanitizeString(fullName);
    if (sanitizedFullName.length < 2) {
      toast.error(t("full_name_too_short", "Full name must be at least 2 characters."));
      return;
    }
    
    if (!email || !email.trim()) {
      toast.error(t("email_required", "Email is required."));
      return;
    }
    
    // Validate email format
    if (!isValidEmail(email)) {
      toast.error(t("invalid_email_format", "Please enter a valid email address."));
      return;
    }
    
    // Normalize email
    const normalizedEmail = normalizeEmail(email);
    
    if (!password) {
      toast.error(t("password_required", "Password is required."));
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      toast.error(passwordValidation.errors[0] || t("password_weak", "Password does not meet requirements."));
      return;
    }
    
    if (!confirmPassword) {
      toast.error(
        t("confirm_password_required", "Confirm Password is required.")
      );
      return;
    }
    if (password !== confirmPassword) {
      toast.error(
        t("passwords_do_not_match", "Password and Confirm Password must match.")
      );
      return;
    }
    if (!termsAccepted) {
      const errorMsg = t(
        "must_agree_terms",
        "You must agree to the Terms & Conditions and Privacy Policy."
      );
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    setError(null);
    setLoading(true);

    const formData = new FormData();
    formData.append("full_name", sanitizedFullName);
    formData.append("email", normalizedEmail);
    formData.append("password", password);
    formData.append("device_type", "W");
    formData.append("device_name", "W");
    if (image) {
      formData.append("image", image);
    }
    formData.append("city", city);
    formData.append("latitude", latitude);
    formData.append("longitude", longitude);
    formData.append("state", state);
    formData.append("place_id", placeId);

    setLoading(true);

    await signup(formData);
  };

  // Render the registration form
  return (
    <>
      <div className="flex min-h-screen px-4 sm:px-6 md:px-10 bg-white dark:bg-[#0A0C1C]  items-center justify-center">
        <div className="flex flex-col md:flex-row w-full pt-10">
          {/* Login Section */}
          <div className="flex flex-col w-full items-center justify-center pb-5">
            <div
              className="loginWrapper text-white w-full sm:w-11/12 md:w-2/4 lg:w-2/5 xl:w-2/5 bg-fbfcfc rounded-xl px-5 sm:px-10 py-6 shadow-md"
              style={{ backgroundColor: isDarkMode ? "#0A0C1C" : "#6D29F6" }}
            >
              {/* Logo Section */}
              <div className="text-center py-10 flex flex-col gap-2 justify-center items-center">
                <img
                  src="/logo-bg.png"
                  alt="VibeLinks Logo"
                  className="h-12 sm:h-16"
                />
                <h1 className="text-2xl font-bold">
                  {t("app_name", "VibeLinks")}
                </h1>
              </div>

              {/* Registration Form */}
              <form onSubmit={handleSubmit} className="w-full">
                {/* Profile Picture Section */}
                {/* <div className='flex justify-center mb-6'>
                  <div className='relative'>
                    <div className='w-24 h-24 rounded-full bg-white flex items-center justify-center'>
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt={t('profile_preview', 'Profile Preview')}
                          className='w-full h-full rounded-full object-cover'
                        />
                      ) : (
                        <div className='text-gray-400'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-12 w-12'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <title>{t('profile_icon', 'Profile Icon')}</title>
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className='absolute bottom-0 right-0 bg-purple-500 rounded-full p-1 border-2 border-white'>
                      <label htmlFor='profile-image' className='cursor-pointer'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-5 w-5 text-white'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <title>
                            {t('add_profile_image', 'Add Profile Image')}
                          </title>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                          />
                        </svg>
                        <input
                          id='profile-image'
                          type='file'
                          accept='image/*'
                          onChange={handleImageChange}
                          className='hidden'
                        />
                      </label>
                    </div>
                  </div>
                </div> */}

                {/* Full Name Input */}
                <div className="mb-4">
                  <label className="text-xs mb-1">{t("full_name")}</label>
                  <div className="relative">
                    <input
                      id="full-name"
                      type="text"
                      name="full_name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-2 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor"
                      style={
                        isDarkMode
                          ? { backgroundColor: "#ffffff", color: "#000000" }
                          : { backgroundColor: "#ffffff", color: "#000000" }
                      }
                      placeholder={t("enter_full_name")}
                    />
                  </div>
                  {error?.full_name && (
                    <p className="text-purple-500 text-xs mt-1">
                      {error.full_name[0]}
                    </p>
                  )}
                </div>

                {/* Email Input */}
                <div className="mb-4">
                  <label className="text-xs mb-1">{t("email_address")}</label>
                  <div className="relative">
                    <input
                      id="email"
                      type="text"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-2 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor"
                      style={
                        isDarkMode
                          ? { backgroundColor: "#ffffff", color: "#000000" }
                          : { backgroundColor: "#ffffff", color: "#000000" }
                      }
                      placeholder={t("email_phone")}
                      autoComplete="off"
                    />
                  </div>
                  {error?.email && (
                    <p className="text-purple-500 text-xs mt-1">
                      {error.email[0]}
                    </p>
                  )}
                </div>

                {/* Password Input */}
                <div className="mb-4">
                  <label className="text-xs mb-1">{t("password")}</label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-2 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor"
                      style={
                        isDarkMode
                          ? { backgroundColor: "#ffffff", color: "#000000" }
                          : { backgroundColor: "#ffffff", color: "#000000" }
                      }
                      placeholder={t("enter_password")}
                      autoComplete="new-password"
                    />
                    <span
                      className="absolute text-purple-700 right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEye : faEyeSlash}
                      />
                    </span>
                  </div>
                  {error?.password && (
                    <p className="text-purple-500 text-xs mt-1">
                      {error.password[0]}
                    </p>
                  )}
                </div>

                {/* Confirm Password Input */}
                <div className="mb-4">
                  <label className="text-xs mb-1">
                    {t("confirm_password")}
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-2 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-700"
                      style={
                        isDarkMode
                          ? { backgroundColor: "#ffffff", color: "#000000" }
                          : { backgroundColor: "#ffffff", color: "#000000" }
                      }
                      placeholder={t("enter_confirm_password")}
                      autoComplete="new-password"
                    />
                    <span
                      className="absolute text-purple-700 right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEye : faEyeSlash}
                      />
                    </span>
                  </div>
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="flex items-start mb-6">
                  <div className="flex items-center h-5">
                    {/* <input
                      id="terms"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="w-4 h-4 border border-white rounded accent-[#6d29f6] dark:accent-[#6d29f6]"
                      style={isDarkMode && termsAccepted ? { backgroundColor: '#6d29f6', borderColor: '#6d29f6' } : {}}
                    /> */}

                    <label className="relative flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="termsAccepted"
                        name="termsAccepted"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="peer sr-only"
                      />

                      <div
                        className={`h-5 w-5 border-2 rounded-md flex items-center justify-center 
                  transition-all duration-200 ${
                    termsAccepted
                      ? isDarkMode 
                        ? "bg-white border-white" 
                        : "bg-[#6d29f6] border-white dark:border-[#6d29f6]"
                      : "bg-transparent border-white dark:border-[#6d29f6]"
                  }`}
                      >
                        {termsAccepted && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill={isDarkMode ? "#000000" : "white"}
                            className="w-3 h-3"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8.5 11.793l6.793-6.793a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </label>
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-[12px]">
                      {t(
                        "i_agree_to_terms",
                        "By signing up, you agree to our "
                      )}
                      <a
                        href="/terms&condition"
                        className="underline"
                        target="_blank"
                      >
                        {t("terms_and_condition", "Terms of Use")}
                      </a>{" "}
                      {t("and", "and")}{" "}
                      <a
                        href="/privacy-policy"
                        className="underline"
                        target="_blank"
                      >
                        {t("privacy_policy", "Privacy Policy.")}
                      </a>
                    </label>
                  </div>
                </div>

                {/* Error Message */}
                {/* {error && <p className='text-white text-xs mb-4'>{error}</p>} */}

                {/* Submit Button */}
                <button
                  type="submit"
                  // disabled={loading || loadingAddress}
                  className="w-full flex justify-center items-center py-2 rounded-lg font-semibold hover:bg-opacity-90 transition duration-150"
                  style={{
                    backgroundColor: isDarkMode ? "#6d29f6" : "white",
                    color: isDarkMode ? "white" : "#7c3aed",
                  }}
                >
                  {loading ? (
                    <>
                      <span className="px-2 flex justify-center items-center">
                        <Loader
                          loaderColor={isDarkMode ? "white" : "primaryColor"}
                        />
                      </span>
                      {t("processing", "Processing...")}
                    </>
                  ) : (
                    t("register", "Register")
                  )}
                </button>
              </form>

              {/* Sign In Link */}
              <div className="text-center mt-8">
                <p className="text-sm">
                  {t("already_have_an_account", "Already have an account?")}{" "}
                  <Link to="/login" className="underline font-medium">
                    {t("sign_in", "Sign In")}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RegisterIndex;
